// AIDEV-NOTE: Captures the magic-link token BEFORE analytics, attribution, or the router can
// observe the URL, then strips it. The token is a bearer credential; it must not
// reach Amplitude auto-capture, the service worker, or browser history.
import { isValidEmail } from '@utils/emailValidation';
import { AUTH_FLOW_TTL_MS } from '@utils/authFlowTtl';
import { defineSessionSlot } from '@utils/sessionSlot';
import { featureFlags } from '@config/featureFlags';

export const AUTH_LINK_PATH = '/auth/link';
export const AUTH_DOOR_PATH = '/auth';

/**
 * Matches the magic-link landing route for workbox's NavigationRoute denylist in
 * src/service-worker.ts. Lives here, next to the constant it is built from, so
 * it is reachable from a test: the service worker module itself cannot be
 * imported under vitest (it runs `self`-scoped side effects at import time).
 *
 * Shape notes:
 * - Workbox matches a denylist entry against `url.pathname + url.search`
 *   concatenated, so the path must be followed by `/`, `?` or end-of-string.
 *   That matches /auth/link, /auth/link/ and /auth/link?token=... while leaving
 *   unrelated paths that merely share the prefix alone, e.g. /auth/linkedin.
 * - The `i` flag mirrors the deliberate lowercasing in `captureAuthLinkParams`
 *   below. React Router matches case-insensitively, so /Auth/Link?token=... IS
 *   a magic-link arrival; without the flag the SW would still handle exactly
 *   the mixed-case arrivals the capture treats as token-bearing.
 * - AUTH_LINK_PATH is escaped rather than interpolated raw. It is a plain
 *   literal today with no regex metacharacters, so the two forms are identical,
 *   but a future value containing `.`, `+` or `?` would silently widen or break
 *   the match.
 */
const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * RFC 5321's ceiling on a whole address. Belt to `isValidEmail`'s braces below:
 * the regex forbids whitespace but not length, and the value ends up as on-screen
 * copy.
 */
const MAX_EMAIL_LENGTH = 254;

/**
 * The characters allowed in the DOMAIN half, i.e. everything after the single
 * `@` that `EMAIL_REGEX` guarantees.
 *
 * AIDEV-NOTE [MEETBALL magic-link]: this exists because the shape check alone
 * did NOT hold, and the docblock below was describing a fix that was one
 * character class short. `EMAIL_REGEX` is `/^[^\s@]+@[^\s@]+\.[^\s@]+$/`, and JS
 * `\s` does not cover every code point that RENDERS as blank: U+2800 BRAILLE
 * PATTERN BLANK, U+200B ZERO WIDTH SPACE and U+3164 HANGUL FILLER all pass
 * `[^\s@]` and all paint as whitespace. So
 * `a@x.com<U+2800>Meetball<U+2800>Support:<U+2800>verify<U+2800>at<U+2800>evil.example`
 * passed the shape check, survived `.trim()`, and landed on screen -- because
 * `maskEmail` masks only the LOCAL part and echoes everything after the last `@`
 * VERBATIM. The result was a sentence of the attacker's choosing rendered as
 * body copy AND as the primary button label on app.meetball.fun, on the one
 * screen whose entire job is to obtain an informed click. The `%20` spelling was
 * closed by the shape check; these were not.
 *
 * The fix constrains the value rather than chasing the character class, and it
 * constrains the half that is actually echoed. Punycode keeps internationalised
 * domains reachable (`xn--`... is ASCII), and an internationalised LOCAL part is
 * untouched here because `maskEmail` never prints more than its first character.
 * `EMAIL_REGEX` already guarantees exactly one `@` -- `[^\s@]` on both sides of
 * it -- so "after the `@`" is unambiguous.
 */
const SAFE_EMAIL_DOMAIN = /^[a-z0-9.-]+$/i;

/**
 * AIDEV-NOTE [MEETBALL magic-link]: `email` arrives on the URL, so it is
 * ATTACKER-AUTHORED text, and both routes put it in front of the visitor -- the
 * landing screen renders it (masked) as body copy AND as the primary button
 * label, on the one screen whose entire job is to obtain an informed click.
 * `maskEmail` only masks the LOCAL part; everything after the last `@` is echoed
 * verbatim, so an unvalidated value like
 * `a@example.com%20-%20Meetball%20Security:%20verify%20at%20evil.example` becomes
 * a sentence of the attacker's choosing on app.meetball.fun. Validating at
 * CAPTURE means every downstream consumer -- copy, button label, `login_hint`,
 * the door's prefill -- gets a value of the right shape or nothing, rather than
 * each of them having to remember. A failing value is treated as "no address",
 * which the landing screen already has an honest state for. The logto-ui fork
 * does the same check on the same value at `OneTimeToken/Error.tsx`; this is the
 * side that actually renders it.
 *
 * The shape check is NOT sufficient on its own -- see SAFE_EMAIL_DOMAIN above
 * for the blank-rendering code points that walk straight through `[^\s@]`.
 */
const sanitizeEmailParam = (value: string | null): string | undefined => {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (trimmed.length > MAX_EMAIL_LENGTH || !isValidEmail(trimmed)) return undefined;
  // Exactly one `@`, guaranteed by EMAIL_REGEX, so this is the echoed half.
  const domain = trimmed.slice(trimmed.lastIndexOf('@') + 1);
  return SAFE_EMAIL_DOMAIN.test(domain) ? trimmed : undefined;
};

export const AUTH_LINK_SW_DENYLIST_PATTERN = new RegExp(
  `^${escapeRegExp(AUTH_LINK_PATH)}(?:[/?]|$)`,
  'i',
);

/**
 * Canonical form of a route path for comparison against a fixed list.
 *
 * AIDEV-NOTE [MEETBALL magic-link]: THE single encoding of the case/slash rule.
 * React Router matches `/auth/link/` and `/Auth/Link` to the same route, so an
 * exact string comparison against '/auth/link' let both variants through every
 * auth-page exclusion in the app. Flag off, that meant a full-page `logtoSignIn`
 * redirect on the magic-link route, which unloads the module singleton holding
 * the one-time token. Query and hash are stripped too: callers sometimes hand
 * over pathname+search (e.g. '/auth?from=expired').
 *
 * It lives HERE, next to the capture it governs, rather than in `@utils/auth`
 * where it started: this is the predicate that decides whether a live bearer
 * token stays in the URL, and it used to be written out three times (here, in
 * `captureAuthLinkParams` below, and in the `i` flag on
 * AUTH_LINK_SW_DENYLIST_PATTERN). `@utils/auth` re-exports it so its own
 * consumers are unchanged; that direction creates no cycle, since auth.ts
 * already imports AUTH_DOOR_PATH/AUTH_LINK_PATH from this module.
 */
export const normalizeRoutePath = (pathname: string): string =>
  (pathname.split(/[?#]/u)[0] ?? pathname).replace(/\/+$/u, '').toLowerCase() || '/';

/**
 * The opaque id of the server-side flow context a link may carry.
 *
 * AIDEV-NOTE [MEETBALL magic-link]: `flow` is NOT a credential for an account,
 * but it is a capability: it reads back the intent and destination the visitor
 * had when they asked for the link, and a JOIN_COMMUNITY intent can carry a
 * space invite token inside it. So it is captured and stripped on exactly the
 * same pass as `token` and `email`, and registered in `SECRET_QUERY_PARAMS`
 * (@lib/analytics/urlSecrets) so it is redacted on the arrivals where the strip
 * cannot run -- the hardened in-app webviews named in `stripParams`' catch,
 * which are precisely where this feature's traffic lands.
 *
 * The shape is the server's `secrets.token_urlsafe(32)` output with room to
 * spare. A value that fails it is DROPPED rather than carried: the only thing a
 * malformed id can do downstream is put an attacker-authored string in a request
 * path, and every link we mint matches.
 */
const FLOW_ID_PATTERN = /^[A-Za-z0-9_-]{32,64}$/;

/**
 * Is this the shape of a flow id? The ONE copy of the check: the `flow` a link
 * carries and the router-state entry's `flowId` are the same id (the 202's
 * `flow_id`), so a change to its shape is made here and nowhere else (R-1 of
 * review 5330766393; there were two private copies of the pattern).
 */
export const isFlowId = (value: unknown): value is string =>
  typeof value === 'string' && FLOW_ID_PATTERN.test(value);

const sanitizeFlowParam = (value: string | null): string | undefined =>
  isFlowId(value) ? value : undefined;

interface AuthLinkParams {
  token: string;
  /**
   * The address the link names, or '' when the arrival carried none. Empty is a
   * real, reachable state: only `token` is the credential, and a link that lost
   * its `email` on the way through a mail scanner still redeems. Consumers must
   * treat '' as "we cannot name the account", never as a match -- see
   * AuthLinkLanding's `isSameAccount`.
   */
  email: string;
  /**
   * The server-side flow-context id, when the link carried a valid one. Absent
   * on every link minted without pending context, on every link minted before
   * this shipped, and on any arrival whose `flow` failed its shape check.
   */
  flow?: string;
}

/**
 * The door screen's own arrival params. Not a credential like the token above,
 * but `email` is still an email address, and the same three observers apply:
 * Amplitude's autocapture attaches the full URL to its page-view event (and to
 * session replay), the address sits in session history, and it rides along in
 * any outbound Referer. AuthDoor also scrubs the query in a mount effect, but
 * that is far too late: AuthDoor is lazy-loaded, while Amplitude's init runs
 * from load plus a `requestIdleCallback` with a 2000ms timeout that fires
 * regardless of idle. On a cold or slow connection the capture wins the race.
 */
interface AuthDoorParams {
  email?: string;
  fromExpired: boolean;
}

let captured: AuthLinkParams | undefined;
let capturedDoorParams: AuthDoorParams | undefined;

/**
 * The RELOAD-SURVIVING copy of `captured`, per tab.
 *
 * AIDEV-NOTE [MEETBALL magic-link continuation]: id 4015057801. `captured` is
 * module state, and this module strips the params off the URL at
 * module-evaluation time, so a real document reload used to arrive with BOTH
 * gone: a new module graph with nothing in it, against a URL that no longer
 * mentions the link. `/auth/link` then rendered "that link didn't work" for a
 * link that was alive and waiting. That contradicted the approval screen's
 * whole survive-a-reload exception (rule 3 in `AuthLinkLanding.tsx`) and spec
 * §3.2's "captured params survive a reload through the existing stash seam",
 * which described a stash that did not exist.
 *
 * WHY SESSIONSTORAGE, and the same three reasons `redemptionEmail.ts` gives:
 * the reload is one tab, a per-tab key spans it exactly and dies with the tab
 * rather than waiting for the next person on the profile, and the value must
 * not go back into the URL, where Amplitude's autocapture, session history and
 * any outbound Referer would all see the credential the capture exists to
 * remove.
 *
 * WRITTEN FOR EVERY `/auth/link` ARRIVAL, not just the approval branch,
 * because at capture time -- before the router, before any network call --
 * nothing knows yet which branch this link is. It costs nothing: every other
 * decision consumes in its mount effect (`AuthLinkLanding`'s rule 3), which
 * clears this within the first commit, so in practice only the approval screen
 * ever holds it.
 *
 * AIDEV-NOTE [MEETBALL magic-link continuation]: id 4074231972, and BOTH HALVES
 * ARE NOW GATED ON `magicLinkContinuation`. This note used to argue the
 * opposite, in two steps that were each wrong.
 *
 * "It cannot be gated where it is written" was about the BRANCH, not the flag:
 * `captureAuthLinkParams` runs from a bare side-effect import in
 * `src/index.tsx`, before the router exists, so it cannot know whether this
 * arrival is an approval or a legacy redemption. The feature flag is a
 * build-time constant and is perfectly readable there.
 *
 * "It SHOULD not be gated either, because the read half is what stops a plain
 * magic-link RELOAD rendering 'that link didn't work' for a live link" is the
 * substantive half, and it is true -- which is exactly why it is not this PR's
 * to ship. That changes what a one-time link does on the SHIPPED magic-link
 * feature: before, a reload of `/auth/link` was a dead end; with the read on, it
 * recovers the token and continues the redemption. A real improvement, a real
 * product decision, and one nobody has taken. It rides in a dark PR whose whole
 * merge argument is that the flag-off build does not move.
 *
 * So the read is gated until that decision is made, and the WRITE is gated with
 * it. The write is not inert on its own: it puts a live one-time token in this
 * tab's `sessionStorage` where the merge base kept it in a module closure, for
 * the window the residual paragraph below bounds. Keeping it while the only
 * reader is off would be a new place the credential lives, bought for nothing.
 * Both halves flip together when the flag does.
 *
 * ON THE FLOW TTL, so a stash can never outlive the token it describes. The
 * envelope is read through `AUTH_FLOW_TTL_MS`, the same clock the per-tab
 * copies and the five mirrors run on (`@utils/authFlowTtl`).
 *
 * THE RESIDUAL, accepted and wider than what it replaces: a visitor who leaves
 * the approval screen without acting -- in-SPA, or by navigating this tab to
 * another site -- leaves the token in this tab's `sessionStorage` until the
 * TTL, this tab's next `/auth/link` mount, a sign-out sweep
 * (`storageCleanup.ts` lists the key) or the tab closing. Rule 3 already
 * accepted that class for the in-SPA case; what is new is the cross-site one.
 * It is the price of the reload working at all, and it is bounded by the same
 * clock as the token. Note this REPLACED a `pagehide` consume that looked like
 * it narrowed the window and could not: `pagehide` with `persisted === false`
 * fires on an ordinary RELOAD as well as on a departure, so the only thing it
 * reliably did was destroy the params the reload was about to need.
 */
export const AUTH_LINK_PARAMS_STASH_KEY = 'meetball_auth_link_params';

/**
 * Is `value` a shape this module could actually have written? A type guard that
 * answers yes or no and NEVER rebuilds the value: a reject is a reject of the
 * WHOLE envelope rather than a per-field degrade.
 *
 * AIDEV-NOTE [MEETBALL magic-link continuation]: F2 of
 * round 3's finding F2. The stash moved a live login token out of a
 * module closure and into same-origin web storage, which makes this key
 * WRITABLE by anything running on the origin -- and what comes back out of it
 * becomes `login_hint`, the landing screen's body copy, and a redemption. So
 * it is read the way `continuation.ts` reads its own stored list: whole-value
 * validation against the shape the writer produces.
 *
 * Rejecting whole is the point of difference from the URL path, and it is not
 * an inconsistency. On the URL, a malformed `email` means "a visitor or a mail
 * client mangled a real link", and "treat it as no address" is the honest
 * reading. In the stash it means the envelope was not written by
 * `captureAuthLinkParams`, which sanitises BEFORE it stores -- so the
 * sanitisers are idempotent over anything we wrote (a trimmed email trims to
 * itself, a valid flow id is still valid), and a value that fails one is
 * evidence about the writer, not about the field. Idempotence is also what lets
 * this be a guard: `sanitizeEmailParam(email) === email` holds for everything
 * we stored, so nothing needs rewriting on the way out.
 *
 * `email: ''` is the one value allowed past the sanitiser, because it is
 * exactly what the writer stores for a token-only link (`isLinkAccountUnnamed`
 * on the landing screen).
 *
 * `token` is NOT shape-checked beyond "a non-empty string", deliberately: the
 * URL path does not constrain it either (Logto owns its format, and a
 * constraint invented here would reject live tokens on a reload, i.e. break
 * the sign-in to protect a nicety). That leaves one residual for §8's
 * pre-production gate, which is where the whole stash exposure is signed off.
 */
const isStashedAuthLinkParams = (value: unknown): value is AuthLinkParams => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }
  // Exactly the writer's fields: the guard returns the stored object itself, so
  // anything else a planted entry carried would otherwise ride along.
  if (
    !Object.keys(value).every(field => field === 'token' || field === 'email' || field === 'flow')
  ) {
    return false;
  }
  const candidate = value as Partial<Record<keyof AuthLinkParams, unknown>>;
  if (typeof candidate.token !== 'string' || candidate.token.length === 0) {
    return false;
  }
  if (
    typeof candidate.email !== 'string' ||
    (candidate.email !== '' && sanitizeEmailParam(candidate.email) !== candidate.email)
  ) {
    return false;
  }
  return candidate.flow === undefined || isFlowId(candidate.flow);
};

/**
 * The RELOAD-SURVIVING copy of `captured`, read through `AUTH_FLOW_TTL_MS` so a
 * stash can never outlive the token it describes. The slot's storage errors
 * never escape, and that is load-bearing rather than habit: iOS Safari with
 * "Block All Cookies" throws on the `sessionStorage` ACCESS itself, and this
 * runs at module evaluation on the one route whose alternative is "the magic
 * link just doesn't work". A lost stash costs a reload; a throw here costs the
 * sign-in.
 */
const stashSlot = defineSessionSlot({
  key: AUTH_LINK_PARAMS_STASH_KEY,
  ttlMs: AUTH_FLOW_TTL_MS,
  isValue: isStashedAuthLinkParams,
});

/**
 * Removes `names` from the current URL's query string, preserving every other
 * param (attribution reads UTMs off this same URL moments later), the path, and
 * the fragment.
 *
 * The fragment is preserved deliberately, and not just for consistency with
 * `storeRedirectPath`, which treats one as first-class. A fragment is the one
 * part of a URL that never leaves the client: it does not reach an edge log, a
 * Referer header or a server access log. That makes it the only place a token
 * could reasonably live if that tradeoff is ever revisited, so this function
 * must not be the thing that eats it.
 */
const stripParams = (params: URLSearchParams, names: readonly string[]): void => {
  if (!names.some(name => params.has(name))) return;
  for (const name of names) {
    params.delete(name);
  }
  const remaining = params.toString();
  try {
    window.history.replaceState(
      null,
      '',
      window.location.pathname + (remaining ? `?${remaining}` : '') + window.location.hash,
    );
  } catch {
    // Never let the app's FIRST import take the whole render down.
    // bootstrapAuthLinkParams.ts calls this at module-evaluation time, before
    // createRoot().render() and before any error boundary exists, so a throw
    // here is a permanently blank page -- on /auth/link, the one route where
    // the alternative is "the magic link just doesn't work". replaceState is
    // not exception-free in the wild: sandboxed iframes and hardened in-app
    // webviews (the mail-client viewers this feature's traffic goes through)
    // restrict history access, and Safari throws past ~100 calls in 30s.
    // Swallowing degrades to "the params stay in the URL", which AuthDoor's and
    // AuthLinkLanding's own scrub effects then clean up.
  }
};

/**
 * Captures and strips the sensitive query params on both magic-link routes.
 * Called once from src/index.tsx before analytics, attribution or the router
 * can observe the URL.
 */
export const captureAuthLinkParams = (): void => {
  // Trailing slash and case normalized (see normalizeRoutePath): React Router matches
  // case-insensitively by default, so a mixed-case arrival renders the screen either way.
  // Failing open on either would leave the token in the URL for the SW/analytics/history
  // for the rest of the session.
  const path = normalizeRoutePath(window.location.pathname);
  const params = new URLSearchParams(window.location.search);

  if (path === AUTH_LINK_PATH) {
    if (!captured) {
      const token = params.get('token');
      const email = sanitizeEmailParam(params.get('email'));
      // THE TOKEN IS STILL CAPTURED ALONE, but no longer because it can redeem
      // on its own -- it cannot. Logto's Experience API requires an identifier
      // alongside the token (`OneTimeTokenVerificationVerifyPayload` in
      // @logto/schemas: `{ identifier, token }`), so the logto-ui fork's
      // `if (!token || !email)` guard is not a policy we can relax from this
      // side. What capturing the token alone buys is the HONEST failure: the
      // landing screen can say "this link is incomplete, here is a fresh one"
      // instead of handing the visitor a Continue button that dead-ends on
      // logto-ui's generic error page with no address to prefill the retry. It
      // also means the live credential is still removed from the URL, history
      // and any outbound Referer on the way. See AuthLinkLanding's
      // `isLinkAccountUnnamed` branch.
      const flow = sanitizeFlowParam(params.get('flow'));
      if (token) {
        captured = { token, email: email ?? '', ...(flow ? { flow } : {}) };
        if (featureFlags.magicLinkContinuation) {
          stashSlot.write(captured);
        }
      } else if (featureFlags.magicLinkContinuation) {
        // No token in the URL: this is either a RELOAD of a document whose
        // first load already stripped them, or a bare visit. The stash tells
        // the two apart -- and it is only ever read here, so a decision that
        // consumed its params cannot have them come back.
        captured = stashSlot.read() ?? undefined;
      }
    }
    stripParams(params, ['token', 'email', 'flow']);
    return;
  }

  if (path === AUTH_DOOR_PATH) {
    if (!capturedDoorParams) {
      // Same sanitizer as the landing route above: this value prefills the
      // door's email field and is handed to Logto as `loginHint`, so a malformed
      // one is worth nothing and a crafted one is worth less than nothing. It
      // also folds in the old `|| undefined`, which existed because `?email=`
      // yields '' rather than null and `??` would have stashed the empty string
      // while the strip below still destroyed `from=expired`.
      const email = sanitizeEmailParam(params.get('email'));
      const fromExpired = params.get('from') === 'expired';
      // A bare /auth arrival stashes nothing, so AuthDoor's fallback to
      // useSearchParams stays in charge of internal navigations.
      if (email || fromExpired) {
        capturedDoorParams = { ...(email ? { email } : {}), fromExpired };
      }
    }
    // `flow` is stripped here too, and it is NOT captured: this route has no use
    // for a flow id, only `/auth/link` redeems one.
    //
    // AIDEV-NOTE [MEETBALL magic-link]: the list was ['email', 'from'], so a
    // `/auth?...&flow=<id>` arrival kept the id in the address bar, in session
    // history, in `useSearchParams` and in any outbound Referer -- the exact four
    // places the landing route goes out of its way to remove it from, on a route
    // that cannot even use it. Nothing we mint puts `flow` on the door URL, which
    // is precisely why it must be stripped rather than trusted: an id only
    // arrives here because somebody put it there (a hand-edited URL, a mail
    // client rewriting the path, a bounce from the hosted UI), and the id is a
    // capability that reads back the pending intent, invite token and all.
    stripParams(params, ['email', 'from', 'flow']);
  }
};

export const getAuthLinkParams = (): AuthLinkParams | undefined => captured;

export const getAuthDoorParams = (): AuthDoorParams | undefined => capturedDoorParams;

/**
 * Drops the door stash. Deliberately separate from the getter, which components
 * call during render and must stay pure.
 *
 * The stash describes ONE arrival, but it lives as long as the document. Without
 * this, a cold `/auth?email=a&from=expired` would strip the URL, and any later
 * remount of the door in the same document (browser Back, an internal link)
 * would read the stale stash, re-prefill the field and re-fire
 * `magic_link_expired_shown` for an arrival that already happened.
 *
 * AuthDoor calls this from its MOUNT EFFECT, not its body: a render-time consume
 * double-invokes under StrictMode and loses the value on the second render.
 */
export const consumeAuthDoorParams = (): void => {
  capturedDoorParams = undefined;
};

/**
 * Drops the magic-link stash. Same shape and same reasoning as
 * `consumeAuthDoorParams`, with a sharper edge: the value here is a ONE-TIME
 * credential. Without this, a browser Back after the landing screen redirected
 * to Logto would remount AuthLinkLanding in the same document, find the token
 * still sitting in the singleton, and offer to redeem a token that has already
 * been spent. The visitor gets an opaque failure at the far end instead of the
 * "that link didn't work, here is a new one" the landing screen shows when the
 * stash is empty.
 *
 * Called from AuthLinkLanding's MOUNT EFFECT, with the value already read into
 * component state by a `useState` initializer: a render-time consume
 * double-invokes under StrictMode and loses the token on the second render.
 */
export const consumeAuthLinkParams = (): void => {
  captured = undefined;
  // The stash goes with it, or "consumed" would only mean "until the next
  // reload" -- which is the whole point of the key.
  stashSlot.clear();
};
